"use client";

import {
  Body,
  Box,
  BulletList,
  Button,
  CountdownTimer,
  Heading,
  Icon,
  Stack,
  type SxProps,
  centerFlexChildren,
  hFlex,
} from "@biom3/react";
import type { Content, RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import type { WishlistProps } from "@ui-kit";
import { PlayButton } from "@ui-kit/CTAButtons/PlayButton";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import { trackGameDetailsEvent } from "@ui-kit/utils";
import { type ComponentProps, useEffect, useMemo, useState } from "react";
import { notifyError } from "../../../utils/monitoring";
import { RiveFollowButton } from "../RiveFollowButton";

export type GameIntroProps = {
  title: string;
  description: string | RichTextField;
  playLink: string;
  eventCountdown?: Date;
  buttonSize?: ComponentProps<typeof Button>["size"];
  wishlistProps: WishlistProps;
  withWishlistCaption?: boolean;
  handleUnhideQuests?: () => void;
  gameId?: string;
  game: Content.GameDocument;
  sx?: SxProps;
  useVariantBExperiment?: boolean;
  isLoggedIn: boolean;
  triggerLogin: () => void;
};
//import FollowRiveButton from "@/assets/animations/FollowRiveButton.riv";

type ActionButtonsProps = {
  game: Content.GameDocument;
  buttonSize: ComponentProps<typeof Button>["size"];
  isLoading: boolean;
  onFollowClick: () => void;
  showFollowButton: boolean;
  wishlistProps: WishlistProps;
  isCreatingWalletGemState: boolean;
  showBasicFollowButtonLoading: boolean;
  setFollowButtonClicked: (clicked: boolean) => void;
  useVariantBExperiment: boolean;
};

function ActionButtons({
  game,
  buttonSize,
  isLoading,
  onFollowClick,
  showFollowButton,
  wishlistProps,
  isCreatingWalletGemState,
  showBasicFollowButtonLoading,
  setFollowButtonClicked,
  useVariantBExperiment,
}: ActionButtonsProps) {
  const gameId = game.uid;
  const tier = game.data.template_tier;
  const { isWishlisted } = wishlistProps;

  const buttons: { key: string; element: React.ReactNode }[] = [];
  const showPrimaryFollow =
    ["overtake"].includes(gameId || "") || tier === "premium";
  const showRive =
    showFollowButton && gameId !== "overtake" && tier !== "premium";

  const onAnalyticsEvent = (link: string) => {
    trackGameDetailsEvent({
      game,
      identifier: "GameIntro",
      control: "PlayNowClicked",
      controlType: "Button",
      extras: {
        website: link,
      },
      action: "Pressed",
    });
  };

  if (gameId === "overtake") {
    buttons.push({
      key: "play",
      element: (
        <PlayButton
          key="play"
          text="Play"
          playLink={"https://overtake.world/games"}
          buttonSize={buttonSize}
          analyticsEvent={onAnalyticsEvent}
          sx={{ w: ["100%", "100%", "auto"] }}
        />
      ),
    });
  }

  if (showPrimaryFollow) {
    buttons.push({
      key: "primary-follow",
      element: (
        <Button
          key="primary-follow"
          size="large"
          variant="primary"
          sx={{
            width: ["100%", "100%", "auto"],
            ...(isWishlisted
              ? {
                  bg: "base.color.status.success.bright",
                  "&:hover:not([disabled])": {
                    boxShadow: ({ base }) =>
                      `inset 0 0 0 ${base.border.size[300]} ${base.color.status.success.bright}`,
                  },
                  "&::before": {
                    bg: "base.color.status.success.bright",
                  },
                }
              : {}),
            justifyContent: "stretch",
            ".Button__innerSpan": { w: "100%" },
            ".Button__innerSpan__innerContent": { w: "100%" },
          }}
          disabled={
            isCreatingWalletGemState ||
            wishlistProps.gemsState === "pending" ||
            showBasicFollowButtonLoading
          }
          onClick={() => {
            setFollowButtonClicked(true);
            wishlistProps.onWishlistClick("GameIntro");
          }}
        >
          {showBasicFollowButtonLoading ? (
            <Button.Icon icon="Loading" />
          ) : (
            <Button.Icon
              icon={isWishlisted ? "Tick" : "Add"}
              iconVariant="bold"
            />
          )}

          <Stack alignItems="center" gap="0px" rc={<span />}>
            {isCreatingWalletGemState
              ? "Creating your wallet"
              : isWishlisted || showBasicFollowButtonLoading
                ? "Following"
                : "Follow now"}

            {isCreatingWalletGemState && (
              <Body size="xSmall" sx={{ c: "inherit" }}>
                Hang tight, it may take a minute
              </Body>
            )}
            {wishlistProps.gemsState === "alreadyClaimed" && (
              <Body size="xSmall" sx={{ c: "inherit" }}>
                Gems already claimed
              </Body>
            )}
            {wishlistProps.gemAmount !== undefined &&
              wishlistProps.gemAmount !== 0 &&
              (wishlistProps.gemsState === "claimed" ||
                wishlistProps.gemsState === "unclaimed") && (
                <Body
                  size="xSmall"
                  sx={{
                    c: "inherit",
                    ...hFlex,
                    ...centerFlexChildren,
                  }}
                >
                  {wishlistProps.gemsState === "claimed" ? "You earned" : ""}
                  {` +${wishlistProps.gemAmount} `}
                  <Icon
                    icon="ImxRewards"
                    sx={{
                      fill: "inherit",
                      w: "base.icon.size.100",
                      mr: "base.spacing.x1",
                    }}
                    variant="bold"
                  />
                  {wishlistProps.gemAmount === 1 ? "gem" : "gems"}
                </Body>
              )}
          </Stack>
        </Button>
      ),
    });
  }

  if (showRive) {
    buttons.push({
      key: "rive-follow",
      element: (
        <RiveFollowButton
          isWishlisted={isWishlisted}
          isLoading={isLoading}
          onClick={onFollowClick}
          game={game}
        />
      ),
    });
  }

  return (
    <Stack
      direction={["column", "column", "row"]}
      gap="base.spacing.x4"
      sx={{
        pt: useVariantBExperiment ? "0px" : "base.spacing.x8",
        display: "flex",

        width: "100%",
        justifyContent: useVariantBExperiment ? "center" : "auto",
      }}
    >
      {buttons.map(({ key, element }) => (
        <Box key={key}>{element}</Box>
      ))}
    </Stack>
  );
}

export function GameIntro({
  title,
  description,
  eventCountdown,
  wishlistProps,
  buttonSize = "medium",
  gameId,
  game,
  sx,
  useVariantBExperiment = false,
  isLoggedIn,
  triggerLogin,
}: GameIntroProps) {
  const { isWishlisted } = wishlistProps;
  const [isLoading, setIsLoading] = useState(false);
  const isSmallScreenMode = useIsLessThanMediumScreen();
  const [followButtonClicked, setFollowButtonClicked] = useState(false);
  const [showFollowButton, setShowFollowButton] = useState(!isWishlisted);

  const rewardsContent = useMemo(() => {
    return (
      <Body
        size="xSmall"
        sx={{
          c: "inherit",
          display: "inline-flex",
          alignItems: "center",
          color: "base.color.text.body.secondary",
          minWidth: "240px",
          w: ["100%", "100%", "auto"],
          justifyContent: useVariantBExperiment ? "center" : "none",
        }}
        rc={<div />}
      >
        Plus {gameId === "ravenquest" && "+75"}
        {gameId === "chessuniverse" && "+35"}
        {gameId === "overtake" && "+85"}
        {gameId === "badmad-robots" && "+35"}
        {gameId === "king-arthur-legends-rise" && "+35"}
        {gameId === "norma-in-metaland" && "+45"}
        {gameId === "dark-machine" && "+40"}
        {gameId === "immortal-rising-2" && "+40"}
        {gameId === "swords-of-blood" && "+40"}
        {gameId === "marblex" && "+55"}
        {gameId === "champignons-of-arborethia" && "+40"}
        {gameId === "treeverse" && "+40"}
        {gameId === "echoes-of-cryptalia" && "+40"}
        {gameId === "gods-unchained" && "+40"}
        {gameId === "somnis-rumble-rush" && "+45"}
        {gameId === "zipclash" && "+40"}
        {gameId === "bitpool" && "+50"}
        {gameId === "tokyo-beast" && "+50"}
        {gameId === "battle-of-souls" && "+40"}
        {gameId === "the-galaxy-of-lemuria" && "+50"}
        {gameId === "inmatch3" && "+50"}
        {gameId === "overknights" && "+50"}
        {gameId === "battlerise" && "+40"}
        {gameId === "chumbi-valley" && "+50"}
        <Icon
          icon="ImxRewards"
          sx={{
            fill: "base.color.text.body.secondary",
            w: "base.icon.size.100",
            mr: "base.spacing.x1",
          }}
          variant="bold"
        />
        Gems up for grabs
      </Body>
    );
  }, [gameId, useVariantBExperiment]);

  // Show the basic follow button if the giveaway panel is not visible, or if the user has already claimed the prize but haven't followed yet
  const isCreatingWalletGemState = wishlistProps.gemsState === "creatingWallet";
  const showBasicFollowButtonLoading = useMemo(() => {
    // Reset the clicked status of the button, incase the user tries to refollow
    if (isWishlisted) setFollowButtonClicked(false);

    if (isCreatingWalletGemState) {
      return true;
    }

    return followButtonClicked;
  }, [isWishlisted, followButtonClicked, isCreatingWalletGemState]);

  const handleFollowButtonClick = async () => {
    setIsLoading(true);

    try {
      if (!isLoggedIn) {
        await triggerLogin();
      }
      await wishlistProps.onWishlistClick("GameIntro");
    } catch (err) {
      notifyError(err, "riveFollowButton");
    } finally {
      setIsLoading(false);
    }
  };

  // Delay the follow button from showing until the rive animation is complete
  useEffect(() => {
    if (!isWishlisted) {
      setShowFollowButton(true);
      return;
    }
    const timer = setTimeout(() => {
      setShowFollowButton(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isWishlisted]);

  return (
    <Stack
      testId="GameIntro"
      direction="column"
      gap={isSmallScreenMode ? "base.spacing.x2" : "base.spacing.x4"}
      sx={{
        w: { default: "100%", large: "70%", xLarge: "75%" },
        px: { default: "none", large: "base.spacing.x12" },
        pt: {
          default: isSmallScreenMode ? "none" : "base.spacing.x3",
          large: "base.spacing.x6",
        },
        maxw: "800px",
        ...sx,
      }}
    >
      {!useVariantBExperiment && (
        <Heading size={isSmallScreenMode ? "medium" : "xLarge"} weight="bold">
          {title}
        </Heading>
      )}

      {eventCountdown && eventCountdown.getTime() > new Date().getTime() && (
        <CountdownTimer
          sx={{
            width: "300px",
            py: isSmallScreenMode ? "base.spacing.x2" : "base.spacing.x5",
          }}
          futureTarget={eventCountdown}
          size="large"
          variant="inverse"
        />
      )}

      {((useVariantBExperiment && !isWishlisted && !isSmallScreenMode) ||
        !useVariantBExperiment) && (
        <Body
          sx={{
            w: useVariantBExperiment ? "100%" : "80%",
            color: "base.color.text.body.secondary",
          }}
          size="medium"
          rc={<div />}
        >
          {typeof description === "string" ? (
            description
          ) : (
            <>
              <PrismicRichText
                field={description}
                components={{
                  list: (p) => (
                    <BulletList
                      sx={{ "& > ul": { gap: "base.spacing.x2" } }}
                      {...p}
                    />
                  ),
                  listItem: ({ key, ...p }) => (
                    <BulletList.Item {...p} key={key} />
                  ),
                  heading4: ({ key, ...p }) => (
                    <Heading
                      {...p}
                      key={key}
                      size="medium"
                      weight="regular"
                      color="primary"
                      sx={{ p: "base.spacing.x1" }}
                    />
                  ),
                  heading5: ({ key, ...p }) => (
                    <Heading
                      {...p}
                      key={key}
                      size="small"
                      weight="regular"
                      color="primary"
                      sx={{ p: "base.spacing.x1" }}
                    />
                  ),
                  heading6: ({ key, ...p }) => (
                    <Heading
                      {...p}
                      key={key}
                      size="xSmall"
                      weight="regular"
                      color="primary"
                      sx={{ p: "base.spacing.x1" }}
                    />
                  ),
                  paragraph: ({ key, ...p }) => (
                    <Body
                      {...p}
                      key={key}
                      size="medium"
                      sx={{
                        color: "base.color.text.body.secondary",
                        display: "block",
                        py: "base.spacing.x1",
                      }}
                      rc={<p />}
                    />
                  ),
                }}
              />
            </>
          )}
        </Body>
      )}

      <ActionButtons
        game={game}
        buttonSize={buttonSize}
        isLoading={isLoading}
        onFollowClick={handleFollowButtonClick}
        showFollowButton={showFollowButton}
        wishlistProps={wishlistProps}
        isCreatingWalletGemState={isCreatingWalletGemState}
        showBasicFollowButtonLoading={showBasicFollowButtonLoading}
        setFollowButtonClicked={setFollowButtonClicked}
        useVariantBExperiment={useVariantBExperiment}
      />

      {!isWishlisted && rewardsContent}
    </Stack>
  );
}
